/**
 * Keyboard drag-and-drop state machine for KanbanBoard.
 *
 * Pure logic, no React — kanban-board.tsx maps card key events onto
 * `handleKanbanKey` and applies the returned action: a state transition for
 * grab/move, an aria-live announcement string, and — on drop — the exact
 * (item, fromColumnId, toColumnId) tuple the pointer-drop path fires, so the
 * keyboard and mouse routes stay behaviourally identical. Kept in a plain
 * .ts module so the `node --test` sweep can exercise the full
 * grab → move → drop/cancel contract without a DOM.
 */

/** Column shape the machine needs — derived from KanbanColumn at the call site. */
export interface KeyboardDndColumn {
  id: string;
  title: string;
  itemCount: number;
}

/** A card picked up via keyboard, plus its current target position. */
export interface KeyboardDndState<T> {
  item: T;
  fromColumnId: string;
  fromColumnIndex: number;
  fromItemIndex: number;
  /** Target column index the card would land in if dropped now. */
  columnIndex: number;
  /** Target position within the target column (insertion slot). */
  itemIndex: number;
}

/** The onDrop tuple — identical to what the pointer path passes. */
export interface KeyboardDropResolution<T> {
  item: T;
  fromColumnId: string;
  toColumnId: string;
}

export type KanbanKeyboardAction<T> =
  | { type: "grabbed"; state: KeyboardDndState<T>; announcement: string }
  | { type: "moved"; state: KeyboardDndState<T>; announcement: string }
  | { type: "dropped"; drop: KeyboardDropResolution<T> | null; announcement: string }
  | { type: "cancelled"; announcement: string }
  /** Idle Enter on a board that also has a click action — the caller fires
   *  onCardClick for the focused card (grab stays on Space). */
  | { type: "activated" }
  | { type: "ignored" };

/** Behaviour switches for {@link handleKanbanKey}. */
export interface KanbanKeyOptions {
  /**
   * Disambiguates the idle Enter-vs-grab conflict: when the board ALSO has a
   * card click action, Enter on an idle card must activate it (the standard
   * button contract for `role="button"`), while Space remains the grab key —
   * otherwise keyboard users can never open a card that pointer users open by
   * clicking. While a card is grabbed, Enter and Space both drop regardless.
   * Defaults to false (no click action: Enter and Space both grab, the
   * original behaviour).
   */
  enterActivates?: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Highest valid insertion index in a column. The origin column already holds
 * the grabbed card (positions 0..count-1); any other column gains a slot at
 * the end (0..count).
 */
function maxItemIndex(
  columns: KeyboardDndColumn[],
  columnIndex: number,
  fromColumnIndex: number,
): number {
  const count = columns[columnIndex]?.itemCount ?? 0;
  return columnIndex === fromColumnIndex ? Math.max(count - 1, 0) : count;
}

export function grabItem<T>(
  item: T,
  columnIndex: number,
  itemIndex: number,
  columns: KeyboardDndColumn[],
): KeyboardDndState<T> {
  return {
    item,
    fromColumnId: columns[columnIndex]?.id ?? "",
    fromColumnIndex: columnIndex,
    fromItemIndex: itemIndex,
    columnIndex,
    itemIndex,
  };
}

export function moveAcrossColumns<T>(
  state: KeyboardDndState<T>,
  delta: number,
  columns: KeyboardDndColumn[],
): KeyboardDndState<T> {
  if (columns.length === 0) return state;
  const columnIndex = clamp(state.columnIndex + delta, 0, columns.length - 1);
  const itemIndex = clamp(state.itemIndex, 0, maxItemIndex(columns, columnIndex, state.fromColumnIndex));
  return { ...state, columnIndex, itemIndex };
}

export function moveWithinColumn<T>(
  state: KeyboardDndState<T>,
  delta: number,
  columns: KeyboardDndColumn[],
): KeyboardDndState<T> {
  const itemIndex = clamp(
    state.itemIndex + delta,
    0,
    maxItemIndex(columns, state.columnIndex, state.fromColumnIndex),
  );
  return { ...state, itemIndex };
}

/**
 * The drop outcome for the current target. Returns null when the card would
 * land back in its origin column — the pointer path treats that as a no-op
 * (onDrop only fires across columns), and the keyboard path must match.
 */
export function resolveDrop<T>(
  state: KeyboardDndState<T>,
  columns: KeyboardDndColumn[],
): KeyboardDropResolution<T> | null {
  const target = columns[state.columnIndex];
  if (!target || target.id === state.fromColumnId) return null;
  return { item: state.item, fromColumnId: state.fromColumnId, toColumnId: target.id };
}

export function announceGrab<T>(
  state: KeyboardDndState<T>,
  columns: KeyboardDndColumn[],
): string {
  const column = columns[state.fromColumnIndex];
  const count = column?.itemCount ?? 0;
  return (
    `Picked up card, position ${state.fromItemIndex + 1} of ${Math.max(count, 1)} in "${column?.title ?? "?"}". ` +
    "Use arrow keys to move, Enter to drop, Escape to cancel."
  );
}

export function announceMove<T>(
  state: KeyboardDndState<T>,
  columns: KeyboardDndColumn[],
): string {
  const column = columns[state.columnIndex];
  const slots = maxItemIndex(columns, state.columnIndex, state.fromColumnIndex) + 1;
  return `Over "${column?.title ?? "?"}", position ${state.itemIndex + 1} of ${slots}.`;
}

export function announceDrop<T>(
  state: KeyboardDndState<T>,
  columns: KeyboardDndColumn[],
): string {
  const target = columns[state.columnIndex];
  if (!target || target.id === state.fromColumnId) {
    const origin = columns[state.fromColumnIndex];
    return `Card dropped back in "${origin?.title ?? "?"}".`;
  }
  return `Card dropped in "${target.title}".`;
}

export function announceCancel<T>(
  state: KeyboardDndState<T>,
  columns: KeyboardDndColumn[],
): string {
  const origin = columns[state.fromColumnIndex];
  return `Move cancelled. Card returned to "${origin?.title ?? "?"}".`;
}

/**
 * Single entry point the board's onKeyDown delegates to.
 *
 * `focused` describes the card that received the key event; it is only used
 * to start a grab — once a card is grabbed, keys operate on the grabbed
 * state (focus stays on the origin card element).
 */
export function handleKanbanKey<T>(
  key: string,
  current: KeyboardDndState<T> | null,
  focused: { item: T; columnIndex: number; itemIndex: number },
  columns: KeyboardDndColumn[],
  options: KanbanKeyOptions = {},
): KanbanKeyboardAction<T> {
  const isEnter = key === "Enter";
  const isSpace = key === " " || key === "Spacebar";
  const activate = isEnter || isSpace;

  if (!current) {
    if (!activate) return { type: "ignored" };
    // Enter-vs-grab disambiguation: with a click action on the board, idle
    // Enter activates the card and only Space grabs (see KanbanKeyOptions).
    if (isEnter && options.enterActivates) return { type: "activated" };
    const state = grabItem(focused.item, focused.columnIndex, focused.itemIndex, columns);
    return { type: "grabbed", state, announcement: announceGrab(state, columns) };
  }

  if (key === "Escape") {
    return { type: "cancelled", announcement: announceCancel(current, columns) };
  }
  if (activate) {
    return {
      type: "dropped",
      drop: resolveDrop(current, columns),
      announcement: announceDrop(current, columns),
    };
  }
  if (key === "ArrowLeft" || key === "ArrowRight") {
    const state = moveAcrossColumns(current, key === "ArrowLeft" ? -1 : 1, columns);
    return { type: "moved", state, announcement: announceMove(state, columns) };
  }
  if (key === "ArrowUp" || key === "ArrowDown") {
    const state = moveWithinColumn(current, key === "ArrowUp" ? -1 : 1, columns);
    return { type: "moved", state, announcement: announceMove(state, columns) };
  }
  return { type: "ignored" };
}
